import { DataTable, Heading, VFlow } from 'bold-ui'
import { TableColumnConfig } from 'bold-ui/lib/components/Table/DataTable/DataTable'
import { format } from 'date-fns'
import { css } from 'emotion'
import { humanizeAge } from 'util/date/humanize-age'
import { isMedicaoAnteriorAntropometria } from 'view/atendimentos/detail/components/modals/medicoes/antropometria/utils-medicaoAnteriorAntropometria'
import { isMedicaoAnteriorExameFisico } from 'view/atendimentos/detail/components/modals/medicoes/exame-fisico/utils-medicaoAnteriorExameFisico'
import {
  HistoricoSectionStyles,
  TipoUnidadeMedicaoForcaMuscular,
} from 'view/atendimentos/detail/historico/model/model-historico'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { hasMedicoes } from '../../../../utils/utils-historico'
import { HistoricoSectionType } from '../../../HistoricoSection'
import HistoricoMedicoesDataTable from './HistoricoMedicoesDataTable'

interface HistoricoMedicoesAnterioresTableBoxProps {
  medicoes: ReadonlyArray<HistoricoMedicaoModel>
  dataNascimentoCidadao: LocalDate
  sectionStyle: HistoricoSectionType
  isAtendimentoPuericultura?: boolean
}

export default function HistoricoMedicoesAnterioresTableBox(props: Readonly<HistoricoMedicoesAnterioresTableBoxProps>) {
  const { medicoes, sectionStyle, dataNascimentoCidadao, isAtendimentoPuericultura } = props

  if (!hasMedicoes(medicoes)) return null

  const medicoesAntropometria = medicoes.filter((medicao) => isMedicaoAnteriorAntropometria(medicao))
  const medicoesExameFisico = medicoes.filter((medicao) => isMedicaoAnteriorExameFisico(medicao))

  const antropometriaColumns = createAntropometriaColumns(isAtendimentoPuericultura, dataNascimentoCidadao)
  const exameFisicoColumns = createExameFisicoColumns(isAtendimentoPuericultura, dataNascimentoCidadao)

  return (
    <VFlow vSpacing={0.5}>
      <Heading
        level={4}
        style={css`
          color: ${sectionStyle && HistoricoSectionStyles[sectionStyle].color};
        `}
      >
        {isAtendimentoPuericultura ? 'Medições anteriores da criança' : 'Medições'}
      </Heading>
      <VFlow vSpacing={1}>
        {medicoesAntropometria.length > 0 && (
          <HistoricoMedicoesDataTable titulo='Antropometria'>
            <DataTable<HistoricoMedicaoModel> columns={antropometriaColumns} rows={medicoesAntropometria} />
          </HistoricoMedicoesDataTable>
        )}
        {medicoesExameFisico.length > 0 && (
          <HistoricoMedicoesDataTable titulo='Exame físico'>
            <DataTable<HistoricoMedicaoModel> columns={exameFisicoColumns} rows={medicoesExameFisico} />
          </HistoricoMedicoesDataTable>
        )}
      </VFlow>
    </VFlow>
  )
}

const createAntropometriaColumns = (
  isAtendimentoPuericultura: boolean,
  dataNascimentoCidadao: LocalDate
): TableColumnConfig<HistoricoMedicaoModel>[] => [
  {
    header: 'Data',
    name: 'dataMedicao',
    style: { width: 85 },
    render: (medicao) => (medicao.dataMedicao ? format(medicao.dataMedicao, 'dd/MM/yyyy') : '-'),
  },

  ...(isAtendimentoPuericultura
    ? [
        {
          header: 'Idade',
          name: 'idade',
          style: { width: 85 },
          render: (medicao: HistoricoMedicaoModel) =>
            medicao.dataMedicao && dataNascimentoCidadao
              ? humanizeAge(dataNascimentoCidadao, medicao.dataMedicao)
              : '-',
        },
      ]
    : []),

  {
    header: 'Peso',
    name: 'valorPeso',
    style: { width: 85 },
    render: (medicao) => (medicao.valorPeso != null ? `${medicao.valorPeso} kg` : '-'),
  },
  {
    header: 'Altura',
    name: 'valorAltura',
    style: { width: 85 },
    render: (medicao) => (medicao.valorAltura != null ? `${medicao.valorAltura} cm` : '-'),
  },
  {
    header: 'IMC',
    name: 'valorImc',
    style: { width: 85 },
    render: (medicao) => (medicao.valorImc != null ? `${medicao.valorImc.toFixed(2)} Kg/m²` : '-'),
  },
  {
    header: 'Per. cefálico',
    name: 'valorPerimetroCefalico',
    style: { width: 85 },
    render: (medicao) => (medicao.valorPerimetroCefalico != null ? `${medicao.valorPerimetroCefalico} cm` : '-'),
  },
]

const createExameFisicoColumns = (
  isAtendimentoPuericultura: boolean,
  dataNascimentoCidadao: LocalDate
): TableColumnConfig<HistoricoMedicaoModel>[] => [
  {
    header: 'Data',
    name: 'dataMedicao',
    style: { width: 240 },
    render: (medicao) => (medicao.dataMedicao ? format(medicao.dataMedicao, 'dd/MM/yyyy') : '-'),
  },

  ...(isAtendimentoPuericultura
    ? [
        {
          header: 'Idade',
          name: 'idade',
          render: (medicao: HistoricoMedicaoModel) =>
            medicao.dataMedicao && dataNascimentoCidadao
              ? humanizeAge(dataNascimentoCidadao, medicao.dataMedicao)
              : '-',
        },
      ]
    : []),
  {
    header: 'Avaliação da força muscular mão direita',
    name: 'valorForcaMuscMaoDireita',
    render: (medicao) =>
      medicao.valorForcaMuscMaoDireita != null
        ? `${medicao.valorForcaMuscMaoDireita} ${TipoUnidadeMedicaoForcaMuscular[medicao.unidadeForcaMuscMaoDireita]}`
        : '-',
  },
  {
    header: 'Avaliação da força muscular mão esquerda',
    name: 'valorForcaMuscMaoEsquerda',
    render: (medicao) =>
      medicao.valorForcaMuscMaoEsquerda != null
        ? `${medicao.valorForcaMuscMaoEsquerda} ${TipoUnidadeMedicaoForcaMuscular[medicao.unidadeForcaMuscMaoEsquerda]}`
        : '-',
  },
]
